import React, { createContext, useContext, useState, ReactNode, FC } from 'react';
import { apiUrl } from '../utils/api';

// --- Types ---
interface User {
  full_name: string;
  email: string;
  access_token: string;
  role?: 'operator' | 'engineer' | 'admin';
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginResponse {
  code: number;
  message: string;
  access_token?: string;
  full_name?: string;
  email?: string;
  role?: 'operator' | 'engineer' | 'admin';
}

// --- Context ---
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// --- Provider ---
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Check localStorage for existing session
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user_data');
    if (savedToken && savedUser) {
      return JSON.parse(savedUser);
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(apiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      // Проверяем, что ответ в формате JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Server returned non-JSON response:', textResponse);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        return false;
      }

      const data: LoginResponse = await response.json();

      if (response.ok && data.code === 0 && data.access_token) {
        const userData: User = {
          full_name: data.full_name || '',
          email: data.email || '',
          access_token: data.access_token,
          role: data.role || 'operator', // Default to operator if role not provided
        };

        setUser(userData);
        localStorage.setItem('auth_token', data.access_token);
        localStorage.setItem('user_data', JSON.stringify(userData));
        return true;
      } else {
        console.error('Login failed:', data.message || 'Unknown error');
        console.error('Response data:', data);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};